import type { ApiPaginatedResponse, ApiResponse } from '@bcube/shared';
import { api } from '@/lib/api';

export enum CreditAnalysisStatus {
  NOT_STARTED = 'NOT_STARTED',
  PENDING_EXTERNAL_DOCS = 'PENDING_EXTERNAL_DOCS',
  PENDING_ANALYSIS = 'PENDING_ANALYSIS',
  IN_ANALYSIS = 'IN_ANALYSIS',
  APPROVED = 'APPROVED',
  PARTIALLY_APPROVED = 'PARTIALLY_APPROVED',
  DOC_DECLINED = 'DOC_DECLINED',
  DECLINED = 'DECLINED',
  EXPIRED = 'EXPIRED',
}

export type CreditAnalysisSituation = 'APPROVED' | 'PARTIALLY_APPROVED' | 'DECLINED';

export interface CreditAnalysis {
  id: string;
  accountId?: string;
  proposalId?: string;
  clientName?: string;
  status: CreditAnalysisStatus;
  paymentMethods: string[];
  creditLimitInCents: number;
  creditAvailableInCents: number;
  newAnalysisIn?: string | 'NEXT_PURCHASE';
  observations: string;
  analystName?: string;
  created_at: string;
  updated_at: string;
  analyzed_at?: string;
  allowedPaymentMethods?: Array<'CASH' | 'INSTALLMENTS'>;
  canUseInstallments?: boolean;
  installmentsRestrictionReason?: string | null;
  documentsStatus?: Record<
    string,
    { status: 'APPROVED' | 'DECLINED'; reason?: string; documentType: string }
  >;
}

export interface CreditAnalysisQueryParams {
  page?: number;
  limit?: number;
  accountId?: string;
  proposalId?: string;
  status?: CreditAnalysisStatus;
  situation?: CreditAnalysisSituation;
  search?: string;
  request_start?: string;
  request_end?: string;
  analysis_start?: string;
  analysis_end?: string;
}

export interface PaginatedResponse<T> {
  docs: T[];
  total: number;
  pages: number;
  limit: number;
}

export interface CreateCreditAnalysisPayload {
  accountId: string;
  proposalId?: string;
  status?: CreditAnalysisStatus;
  documentRequestSent?: boolean;
  documentRequestEmails?: string[];
  documentRequestMessage?: string;
}

export interface TimelineEntry {
  id: string;
  timestamp: string;
  type?: 'audit' | 'historical';
  status: string | null;
  author: {
    name: string;
    avatarUrl?: string;
  };
  observations?: string;
  metadata?:
    | Array<{
        field: string;
        before?: string;
        after?: string;
      }>
    | Record<string, unknown>;
}

export interface CreditAnalysisHistory {
  id: string;
  accountId: string;
  creditAnalysisId: string;
  status: CreditAnalysisStatus;
  paymentMethods: string[];
  creditLimitInCents: number;
  creditAvailableInCents: number;
  newAnalysisIn?: string;
  observations: string;
  analystName: string;
  analyzedAt: string;
  createdAt: string;
}

export const creditAnalysisApi = {
  findMany: async (
    params?: CreditAnalysisQueryParams,
  ): Promise<PaginatedResponse<CreditAnalysis>> => {
    const { data } = await api.get<ApiPaginatedResponse<CreditAnalysis>>(
      '/commercial/credit-analysis',
      { params },
    );
    return {
      docs: data.data,
      total: data.meta.total,
      pages: data.meta.pages,
      limit: data.meta.limit,
    };
  },

  findOne: async (id: string): Promise<CreditAnalysis> => {
    const { data } = await api.get<ApiResponse<CreditAnalysis>>(
      `/commercial/credit-analysis/${id}`,
    );
    return data.data;
  },

  findByAccount: async (accountId: string): Promise<CreditAnalysis | null> => {
    try {
      console.log('[API CREDIT ANALYSIS] findByAccount - accountId:', accountId);
      const { data } = await api.get<ApiResponse<CreditAnalysis>>(
        `/commercial/credit-analysis/by-account/${accountId}`,
      );
      console.log('[API CREDIT ANALYSIS] findByAccount - result:', {
        id: data.data?.id,
        status: data.data?.status,
        proposalId: data.data?.proposalId,
        newAnalysisIn: data.data?.newAnalysisIn,
      });
      return data.data;
    } catch (error) {
      console.log('[API CREDIT ANALYSIS] findByAccount - error:', error);
      return null;
    }
  },

  findByProposal: async (proposalId: string): Promise<CreditAnalysis | null> => {
    try {
      console.log('[API CREDIT ANALYSIS] findByProposal - proposalId:', proposalId);
      const { data } = await api.get<ApiResponse<CreditAnalysis>>(
        `/commercial/credit-analysis/by-proposal/${proposalId}`,
      );
      console.log('[API CREDIT ANALYSIS] findByProposal - result:', {
        id: data.data?.id,
        status: data.data?.status,
        proposalId: data.data?.proposalId,
        newAnalysisIn: data.data?.newAnalysisIn,
      });
      return data.data;
    } catch (error) {
      console.log('[API CREDIT ANALYSIS] findByProposal - error:', error);
      return null;
    }
  },

  sendDocumentRequest: async (payload: {
    proposalId: string;
    accountId?: string;
    emails: string[];
  }): Promise<CreditAnalysis> => {
    const { data } = await api.post<ApiResponse<CreditAnalysis>>(
      '/commercial/credit-analysis/send-document-request',
      payload,
    );
    return data.data;
  },

  resendDocumentRequest: async (payload: {
    emails: string[];
    accountId: string;
    proposalId: string;
    creditAnalysisId: string;
  }): Promise<{ success: boolean; sentCount?: number; failedCount?: number }> => {
    const { data } = await api.post<ApiResponse<{ sentCount?: number; failedCount?: number }>>(
      '/commercial/credit-analysis/resend-document-request',
      payload,
    );
    return { success: true, ...data.data };
  },

  create: async (payload: CreateCreditAnalysisPayload): Promise<CreditAnalysis> => {
    const { data } = await api.post<ApiResponse<CreditAnalysis>>(
      '/commercial/credit-analysis',
      payload,
    );
    return data.data;
  },

  uploadDocuments: async (payload: {
    accountId: string;
    proposalId?: string;
    creditAnalysisId?: string;
    uploadedBy?: 'analyst' | 'client';
    files: Array<{
      fieldId: string;
      fileName: string;
      key: string;
      sizeInBytes: number;
      contentType?: string;
      eTag?: string;
    }>;
  }): Promise<CreditAnalysis> => {
    if (payload.creditAnalysisId) {
      const { data } = await api.post<ApiResponse<CreditAnalysis>>(
        `/commercial/credit-analysis/${payload.creditAnalysisId}/documents`,
        payload,
      );
      return data.data;
    }

    // Se não tem creditAnalysisId, precisa buscar pela proposal ou criar um novo
    // Usa a rota especial de upload que cria/atualiza
    const { data } = await api.post<ApiResponse<CreditAnalysis>>(
      '/commercial/credit-analysis/upload-documents',
      payload,
    );
    return data.data;
  },

  getTimeline: async (creditAnalysisId: string): Promise<TimelineEntry[]> => {
    const { data } = await api.get<ApiResponse<TimelineEntry[]>>(
      `/commercial/credit-analysis/${creditAnalysisId}/timeline`,
    );
    return data.data;
  },

  getHistoryByAccount: async (
    accountId: string,
    page = 1,
    limit = 10,
  ): Promise<{
    docs: CreditAnalysisHistory[];
    total: number;
    pages: number;
    limit: number;
  }> => {
    const { data } = await api.get<ApiPaginatedResponse<CreditAnalysisHistory>>(
      `/commercial/credit-analysis/by-account/${accountId}/history`,
      {
        params: { page, limit },
      },
    );
    return {
      docs: data.data,
      total: data.meta.total,
      pages: data.meta.pages,
      limit: data.meta.limit,
    };
  },

  startAnalysis: async (id: string): Promise<void> => {
    await api.patch(`/commercial/credit-analysis/${id}`, {
      status: CreditAnalysisStatus.IN_ANALYSIS,
    });
  },

  approve: async (
    id: string,
    payload: {
      creditLimitInCents: number | null;
      isUnlimited?: boolean;
      paymentMethods: string[];
      observations?: string;
      newAnalysisIn?: string;
    },
  ): Promise<void> => {
    await api.post(`/commercial/credit-analysis/${id}/approve`, payload);
  },

  decline: async (
    id: string,
    payload: { observations: string; newAnalysisIn?: string },
  ): Promise<void> => {
    await api.post(`/commercial/credit-analysis/${id}/decline`, payload);
  },
};
